import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://odontoassistai.com.br';
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

function normalizePath(pathname) {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function buildCanonical(pathname) {
  const normalized = normalizePath(pathname);
  return normalized === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalized}`;
}

function getSeoData(pathname) {
  const normalized = normalizePath(pathname);

  const publicRoutes = {
    '/': {
      title: 'OdontoAssist AI | Gestão inteligente para clínicas odontológicas',
      description:
        'Plataforma inteligente de gestão para clínicas odontológicas com prontuários, odontograma, documentos, agenda e recursos de apoio com IA.',
      index: true
    },
    '/instalar': {
      title: 'Instalar app | OdontoAssist AI',
      description:
        'Veja como instalar o OdontoAssist AI no celular e acessar a plataforma com praticidade no dia a dia da clínica.',
      index: true
    },
    '/privacidade': {
      title: 'Política de Privacidade | OdontoAssist AI',
      description:
        'Conheça a Política de Privacidade do OdontoAssist AI e como tratamos dados pessoais e informações clínicas.',
      index: true
    },
    '/termos-de-uso': {
      title: 'Termos de Uso | OdontoAssist AI',
      description:
        'Leia os Termos de Uso da plataforma OdontoAssist AI para clínicas e profissionais da odontologia.',
      index: true
    },
    '/lgpd': {
      title: 'LGPD | OdontoAssist AI',
      description:
        'Entenda como o OdontoAssist AI aplica princípios da LGPD no tratamento de dados pessoais e informações sensíveis.',
      index: true
    },
    '/seguranca': {
      title: 'Segurança da Informação | OdontoAssist AI',
      description:
        'Saiba como o OdontoAssist AI protege dados, prontuários e documentos com práticas de segurança da informação.',
      index: true
    },
    '/ajuda': {
      title: 'Central de Ajuda | OdontoAssist AI',
      description:
        'Acesse a Central de Ajuda do OdontoAssist AI com orientações para uso da plataforma.',
      index: true
    },
    '/ajuda/primeiros-passos': {
      title: 'Primeiros passos | OdontoAssist AI',
      description:
        'Aprenda os primeiros passos para configurar e começar a usar o OdontoAssist AI.',
      index: true
    },
    '/ajuda/tutoriais': {
      title: 'Tutoriais | OdontoAssist AI',
      description:
        'Consulte tutoriais do OdontoAssist AI para aproveitar melhor os recursos da plataforma.',
      index: true
    },
    '/ajuda/tutoriais/odontograma': {
      title: 'Tutorial do odontograma | OdontoAssist AI',
      description:
        'Veja como usar o odontograma do OdontoAssist AI no acompanhamento clínico dos pacientes.',
      index: true
    },
    '/ajuda/tutoriais/documentos': {
      title: 'Tutorial de documentos | OdontoAssist AI',
      description:
        'Aprenda a gerar documentos, termos e receituários no OdontoAssist AI.',
      index: true
    },
    '/ajuda/tutoriais/anamnese': {
      title: 'Tutorial de anamnese | OdontoAssist AI',
      description:
        'Saiba como configurar e utilizar a anamnese digital no OdontoAssist AI.',
      index: true
    },
    '/ajuda/tutoriais/configuracoes': {
      title: 'Tutorial de configurações | OdontoAssist AI',
      description:
        'Aprenda a ajustar as configurações da clínica e da conta no OdontoAssist AI.',
      index: true
    },
    '/ajuda/tutoriais/agendamento': {
      title: 'Tutorial de agenda | OdontoAssist AI',
      description:
        'Veja como usar a agenda e os lembretes do OdontoAssist AI na rotina da clínica.',
      index: true
    },
    '/ajuda/tutoriais/extensao-whatsapp': {
      title: 'Tutorial da extensão WhatsApp | OdontoAssist AI',
      description:
        'Aprenda a usar a extensão integrada ao WhatsApp Web no ecossistema do OdontoAssist AI.',
      index: true
    },
    '/contato': {
      title: 'Contato | OdontoAssist AI',
      description:
        'Entre em contato com a equipe do OdontoAssist AI para suporte, dúvidas e informações.',
      index: true
    },
    '/status': {
      title: 'Status do sistema | OdontoAssist AI',
      description:
        'Consulte a página de status do sistema OdontoAssist AI.',
      index: true
    },
    '/login': {
      title: 'Entrar | OdontoAssist AI',
      description:
        'Acesse sua conta no OdontoAssist AI.',
      index: false
    },
    '/forgot-password': {
      title: 'Recuperar senha | OdontoAssist AI',
      description:
        'Solicite a recuperação de senha da sua conta OdontoAssist AI.',
      index: false
    },
    '/public/sign/done': {
      title: 'Assinatura concluída | OdontoAssist AI',
      description:
        'Confirmação de assinatura concluída.',
      index: false
    }
  };

  if (publicRoutes[normalized]) {
    return {
      ...publicRoutes[normalized],
      canonical: buildCanonical(normalized)
    };
  }

  const noindexPrefixes = [
    '/admin',
    '/dashboard',
    '/prontuarios',
    '/agenda',
    '/lembretes',
    '/estoque',
    '/cobranca',
    '/configuracoes',
    '/assinatura',
    '/anamnese',
    '/ia',
    '/imagens',
    '/receituarios',
    '/contratos',
    '/pagamento',
    '/pacientes',
    '/anamnese/publico',
    '/public/sign',
    '/reset-password',
    '/ativar-conta'
  ];

  const mustNoindex = noindexPrefixes.some((prefix) => normalized.startsWith(prefix));

  if (mustNoindex) {
    return {
      title: 'OdontoAssist AI',
      description: 'Área restrita ou página operacional do OdontoAssist AI.',
      canonical: buildCanonical(normalized),
      index: false
    };
  }

  return {
    title: 'OdontoAssist AI',
    description: 'Plataforma inteligente de gestão para clínicas odontológicas.',
    canonical: `${SITE_URL}/`,
    index: false
  };
}

export default function SeoManager() {
  const location = useLocation();
  const seo = getSeoData(location.pathname);
  const robots = seo.index ? 'index,follow' : 'noindex,nofollow';

  return (
    <Helmet prioritizeSeoTags>
      <html lang="pt-BR" />
      <title>{seo.title}</title>

      <meta name="description" content={seo.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={seo.canonical} />

      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="OdontoAssist AI" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:image" content={DEFAULT_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
}